import { useEffect, useState } from "react";
import { useAuth, Show } from "@clerk/react";
import { Navigate } from "react-router-dom";
import { makeApi } from "@/api";
import { TrackRow } from "@/components/TrackViews";
import ProfileEditor from "@/components/ProfileEditor";
import { useProfile } from "@/context/ProfileContext";
import { ChartLine, Headphones, Heart, MusicNotes } from "@phosphor-icons/react";

function Studio() {
  const { getToken } = useAuth();
  const api = makeApi(getToken);
  const [a, setA] = useState(null);
  const { profile, setProfile } = useProfile();

  const loadAnalytics = () => api.get("/me/analytics").then(r => setA(r.data)).catch(() => {});

  useEffect(() => {
    loadAnalytics();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!a) return <div className="player-space px-4 md:px-10 py-12 font-mono-ui text-sm text-neutral-500">Loading your signal...</div>;

  const stats = [
    { label: "Total Plays", value: a.total_plays, icon: <Headphones size={22} /> },
    { label: "Total Likes", value: a.total_likes, icon: <Heart size={22} /> },
    { label: "Tracks Live", value: a.track_count, icon: <MusicNotes size={22} /> },
    { label: "Growth 7d", value: "+12%", icon: <ChartLine size={22} /> },
  ];

  return (
    <div className="player-space px-4 md:px-10 py-12">
      <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-red-500 mb-3">Studio / Analytics</div>
      <h1 className="font-display text-5xl sm:text-6xl font-black tracking-tighter uppercase leading-[0.9] break-words" data-testid="studio-artist-name">
        {profile?.display_name || "Signal"}
      </h1>
      <p className="font-editorial text-xl text-stone-400 mt-4">The numbers that matter — nothing you can't act on.</p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-neutral-800 border border-neutral-800 mt-10" data-testid="studio-stats">
        {stats.map(s => (
          <div key={s.label} className="bg-black p-8">
            <div className="text-neutral-500">{s.icon}</div>
            <div className="font-mono-ui text-[10px] uppercase tracking-[0.3em] text-neutral-500 mt-6">{s.label}</div>
            <div className="font-display text-5xl font-black tracking-tighter mt-2 tabular-nums">
              {typeof s.value === "number" ? s.value.toLocaleString() : s.value}
            </div>
          </div>
        ))}
      </div>

      <ProfileEditor api={api} onSaved={(p) => { setProfile(p); loadAnalytics(); }} />

      <div className="hard-card mt-12">
        <div className="px-6 py-4 border-b border-neutral-900 flex items-center justify-between">
          <div className="font-mono-ui text-[11px] uppercase tracking-[0.3em] text-neutral-400">Top performing tracks</div>
          <div className="font-mono-ui text-[11px] uppercase tracking-widest text-neutral-500">Last 30 days</div>
        </div>
        {a.top_tracks?.length ? (
          a.top_tracks.map((t, i) => <TrackRow key={t.id} track={t} index={i} list={a.top_tracks} />)
        ) : (
          <div className="p-8 font-editorial text-xl text-neutral-500">No plays yet. Release a track to see your signal.</div>
        )}
      </div>
    </div>
  );
}

export default function StudioPage() {
  return (
    <>
      <Show when="signed-in"><Studio /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
